// Shared constants + badge components for the Compliance Tracker pages.
import React from 'react';
import { formatDate } from '../../lib/format';

export const BTN_PRIMARY = 'bg-[#3b4872] hover:bg-[#2d3856] text-white text-xs font-bold px-4 py-2 rounded-sm transition-colors';
export const BTN_SECONDARY = 'border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-sm transition-colors';

export const INSURANCE_TYPES = [
  { key: 'car', label: 'CAR Policy', fullLabel: 'Contractor All Risk (CAR)' },
  { key: 'wc', label: 'Workman Compensation', fullLabel: 'Workman / Employee Compensation Insurance' },
  { key: 'pi', label: 'Professional Indemnity', fullLabel: 'Professional Indemnity Insurance' },
  { key: 'medical', label: 'Medical Insurance', fullLabel: 'Medical / Health Insurance' },
  { key: 'gpa', label: 'Group Personal Accident', fullLabel: 'Group Personal Accident Insurance' },
  { key: 'gratuity', label: 'Group Gratuity', fullLabel: 'Group Gratuity Insurance' },
  { key: 'plant', label: 'Plant & Machinery', fullLabel: 'Plant & Machinery Insurance' },
  { key: 'marine', label: 'Marine Insurance', fullLabel: 'Marine Cargo / Transit Insurance' },
  { key: 'vehicle', label: 'Vehicle Insurance', fullLabel: 'Vehicle Insurance (Personal & Commercial)' },
  { key: 'home', label: 'Home / Office / Asset', fullLabel: 'Home, Office & Asset Insurance (Structure, excl. Land)' },
];

// Canonical policy_type strings (match the AI extraction list) keyed by tab.
export const CANONICAL_POLICY_TYPE = {
  car: 'Contractor All Risk (CAR)',
  wc: 'Workman Compensation',
  pi: 'Professional Indemnity',
  medical: 'Medical Insurance',
  gpa: 'Group Personal Accident',
  gratuity: 'Group Gratuity',
  plant: 'Plant & Machinery Insurance',
  marine: 'Marine Insurance',
  vehicle: 'Vehicle Insurance',
  home: 'Home / Office / Asset Insurance',
};

const TYPE_KEYWORDS = [
  ['car', ['all risk', 'car']],
  ['wc', ['workman', 'employee compensation', 'workmen']],
  ['pi', ['indemnity']],
  ['medical', ['medical', 'health']],
  ['gpa', ['personal accident']],
  ['gratuity', ['gratuity']],
  ['plant', ['plant', 'machinery']],
  ['marine', ['marine', 'transit', 'cargo']],
  ['vehicle', ['vehicle', 'motor']],
  ['home', ['home', 'office', 'asset']],
];

export const policyTypeToTab = (policyType) => {
  const t = (policyType || '').toLowerCase();
  for (const [key, kws] of TYPE_KEYWORDS) {
    if (kws.some(k => t.includes(k))) return key;
  }
  return 'home';
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  return formatDate(iso);
};

export const DaysBadge = ({ days }) => {
  if (days === null || days === undefined) return <span className="text-xs text-slate-400">—</span>;
  if (days < 0) return <span data-testid="days-badge-expired" className="px-2 py-0.5 rounded-sm text-[10px] font-bold bg-red-600 text-white">EXPIRED</span>;
  let cls = 'bg-emerald-100 text-emerald-700';
  if (days < 15) cls = 'bg-rose-100 text-rose-700';
  else if (days <= 60) cls = 'bg-amber-100 text-amber-700';
  return <span data-testid="days-badge" className={`px-2 py-0.5 rounded-sm text-[10px] font-bold ${cls}`}>{days}d left</span>;
};

const STATUS_COLORS = {
  Active: 'bg-emerald-100 text-emerald-700',
  Registered: 'bg-emerald-100 text-emerald-700',
  'Pending Renewal': 'bg-amber-100 text-amber-700',
  Applied: 'bg-sky-100 text-sky-700',
  Required: 'bg-indigo-100 text-indigo-700',
  Expired: 'bg-red-100 text-red-700',
  Cancelled: 'bg-slate-200 text-slate-600',
  'Not Applicable': 'bg-slate-100 text-slate-500',
};

export const StatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-600'}`}>
    {status || '—'}
  </span>
);

export const AiBadge = () => (
  <span data-testid="ai-suggested-badge" className="px-2 py-0.5 rounded-sm text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
    AI Suggested · Review Required
  </span>
);

export const EnforcedBadge = ({ enforced }) => (
  enforced
    ? <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold bg-emerald-100 text-emerald-700">Enforced</span>
    : <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold bg-slate-100 text-slate-500">Not Enforced</span>
);

export const normalizeProjects = (r) => (Array.isArray(r) ? r : r?.projects || r?.items || []);
